/**
 * Purpose: Queue service for managing OG tags API calls with rate limiting and prioritization
 *
 * How it works:
 * 1. Maintains a queue of pending OG tags requests
 * 2. Processes requests at a maximum rate of 1 per 2 seconds
 * 3. Prioritizes requests from the currently active chat
 * 4. Deduplicates identical URLs in the queue
 * 5. Uses a simple fixed-interval rate limiter for requests
 *
 * Key features:
 * - Rate limited to 1 request per 2 seconds
 * - Priority queue for current chat
 * - Request deduplication
 * - Automatic retry on failure
 * - Caching to avoid redundant requests
 */

import { useNavigationState } from "@/zustand/NavigationState";
import { getUrlMetadata, LinkMetadataType } from "./scrapeService";

interface QueuedRequest {
  id: string;
  url: string;
  chatId: string;
  resolve: (value: LinkMetadataType) => void;
  reject: (error: Error) => void;
  timestamp: number;
  retryCount?: number;
}

class OgTagsQueueService {
  private queue: QueuedRequest[] = [];
  private processing = false;
  private lastProcessTime = 0;
  private readonly RATE_LIMIT_MS = 2000; // 1 request per 2 seconds
  private readonly MAX_RETRIES = 2;
  private readonly cache = new Map<
    string,
    { data: LinkMetadataType; timestamp: number }
  >();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

  /**
   * Add a new request to the queue
   * @param url - The URL to fetch OG tags for
   * @param chatId - The chat ID making the request
   * @returns Promise that resolves with the metadata
   */
  async addRequest(url: string, chatId: string): Promise<LinkMetadataType> {
    // Check cache first
    const cached = this.cache.get(url);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    // Check if identical URL is already in queue
    const existingRequest = this.queue.find((req) => req.url === url);
    if (existingRequest) {
      // Chain additional callbacks onto the existing request instead of overwriting them
      const originalResolve = existingRequest.resolve;
      const originalReject = existingRequest.reject;

      return new Promise((resolve, reject) => {
        existingRequest.resolve = (value: LinkMetadataType) => {
          try {
            originalResolve(value);
          } finally {
            resolve(value);
          }
        };

        existingRequest.reject = (error: Error) => {
          try {
            originalReject(error);
          } finally {
            reject(error);
          }
        };
      });
    }

    return new Promise((resolve, reject) => {
      const request: QueuedRequest = {
        id: crypto.randomUUID(),
        url,
        chatId,
        resolve,
        reject,
        timestamp: Date.now(),
        retryCount: 0,
      };

      this.queue.push(request);
      this.sortQueue();
      this.processQueue();
    });
  }

  /**
   * Sort the queue to prioritize current chat requests
   */
  private sortQueue() {
    const activeChatId = useNavigationState.getState().activeChatId;

    this.queue.sort((a, b) => {
      // Prioritize active chat
      if (activeChatId) {
        if (a.chatId === activeChatId && b.chatId !== activeChatId) {
          return -1;
        }
        if (b.chatId === activeChatId && a.chatId !== activeChatId) {
          return 1;
        }
      }

      // Then by timestamp (older requests first)
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Process the queue respecting rate limits
   */
  private async processQueue() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    // Respect rate limit
    const now = Date.now();
    if (now - this.lastProcessTime < this.RATE_LIMIT_MS) {
      setTimeout(
        () => this.processQueue(),
        this.RATE_LIMIT_MS - (now - this.lastProcessTime),
      );
      return;
    }

    this.processing = true;
    this.lastProcessTime = now;

    const request = this.queue.shift();
    if (!request) {
      this.processing = false;
      return;
    }

    try {
      const metadata = await getUrlMetadata(request.url);

      // Cache the result
      this.cache.set(request.url, {
        data: metadata,
        timestamp: Date.now(),
      });

      // Clean up old cache entries periodically
      if (this.cache.size > 100) {
        this.cleanupCache();
      }

      request.resolve(metadata);
    } catch (error) {
      // Retry logic
      if ((request.retryCount ?? 0) < this.MAX_RETRIES) {
        request.retryCount = (request.retryCount ?? 0) + 1;
        request.timestamp = Date.now();
        this.queue.push(request);
        this.sortQueue();
      } else {
        request.reject(error as Error);
      }
    }

    this.processing = false;

    // Schedule next item with a small delay to prevent race conditions
    // Check if we should continue processing before scheduling
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), this.RATE_LIMIT_MS);
    }
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache() {
    const now = Date.now();
    for (const [url, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.CACHE_TTL) {
        this.cache.delete(url);
      }
    }
  }

  /**
   * Clear the queue and cache
   */
  clear() {
    // Reject all pending requests so their promises do not hang indefinitely
    for (const request of this.queue) {
      try {
        request.reject(new Error("Queue cleared"));
      } catch {
        // Swallow any errors from user-provided reject handlers
      }
    }

    this.queue = [];
    this.cache.clear();
    this.processing = false;
  }

  /**
   * Get queue statistics
   */
  getStats() {
    const activeChatId = useNavigationState.getState().activeChatId;
    const activeChatRequests = this.queue.filter(
      (req) => req.chatId === activeChatId,
    ).length;
    const otherRequests = this.queue.length - activeChatRequests;

    return {
      totalInQueue: this.queue.length,
      activeChatRequests,
      otherRequests,
      cacheSize: this.cache.size,
      processing: this.processing,
    };
  }
}

// Singleton instance
export const ogTagsQueueService = new OgTagsQueueService();

/**
 * Wrapper function to use the queue service instead of direct API calls
 * @param url - The URL to fetch OG tags for
 * @param chatId - The chat ID making the request
 * @returns Promise that resolves with the metadata
 */
export const getUrlMetadataWithQueue = (
  url: string,
  chatId: string,
): Promise<LinkMetadataType> => {
  return ogTagsQueueService.addRequest(url, chatId);
};
